import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Shield, Monitor, KeyRound, ListChecks, Users, ScrollText, ShieldCheck, LogOut, AlertTriangle } from "lucide-react";

function Tab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${active ? "bg-[#1a3c5e] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function SessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const load = () => api.get(`/admin/sessions?active_only=${activeOnly}`).then(r => setSessions(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOnly]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this session?")) return;
    await api.post(`/admin/sessions/${id}/revoke`);
    toast.success("Session revoked");
    load();
  };

  return (
    <div className="space-y-3" data-testid="sessions-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#1a3c5e]">All Sessions ({sessions.length})</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="active-only-toggle" />
          Active only
        </label>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="p-3">User</th><th>Role</th><th>UA</th><th>IP</th><th>Login</th><th></th>
          </tr></thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="p-3">{s.user?.name || s.user?.email || s.user_email}</td>
                <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{s.user_role}</span></td>
                <td className="text-xs text-slate-500 truncate max-w-[200px]">{s.user_agent}</td>
                <td className="text-xs text-slate-500">{s.ip}</td>
                <td className="text-xs text-slate-500">{s.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td>
                  {s.active && (
                    <button onClick={() => revoke(s.id)} className="text-rose-600 hover:bg-rose-50 px-2 py-1 rounded text-xs" data-testid={`revoke-${s.id}`}>
                      <LogOut className="w-3 h-3 inline" /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No sessions</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [eventType, setEventType] = useState("");
  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (eventType) params.set("event_type", eventType);
    params.set("limit", "100");
    api.get(`/admin/security/audit-log?${params}`).then(r => setLogs(r.data || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3" data-testid="audit-panel">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg" data-testid="audit-search" />
        <input value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="Event type (e.g., lead.)" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg" data-testid="audit-event-type" />
        <button onClick={load} className="px-4 py-2 bg-[#1a3c5e] text-white rounded-lg" data-testid="audit-search-btn">Search</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500 sticky top-0"><tr>
            <th className="p-3">When</th><th>Actor</th><th>Event</th><th>Entity</th><th>Channel</th>
          </tr></thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2 text-xs text-slate-500">{l.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="text-xs">{l.actor?.name || l.actor_role || "system"}</td>
                <td><span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">{l.event_type}</span></td>
                <td className="text-xs"><span className="text-slate-500">{l.entity_type}</span> · <span className="font-mono">{(l.entity_id || "").slice(0, 10)}</span></td>
                <td className="text-xs">{l.channel}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No events</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PermissionsPanel() {
  const [matrix, setMatrix] = useState({});
  const [users, setUsers] = useState([]);
  const [changingId, setChangingId] = useState("");
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    api.get("/admin/security/permissions").then(r => setMatrix(r.data?.matrix || {}));
    api.get("/admin/designers").then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const changeRole = async () => {
    if (!changingId || !newRole) return;
    try {
      await api.post("/admin/security/users/role", { user_id: changingId, role: newRole });
      toast.success("Role updated");
      setChangingId(""); setNewRole("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed (requires super_admin)");
    }
  };

  return (
    <div className="space-y-4" data-testid="permissions-panel">
      <div className="grid lg:grid-cols-2 gap-4">
        {Object.entries(matrix).map(([role, perms]) => (
          <div key={role} className="p-4 rounded-2xl bg-white border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-[#1a3c5e] capitalize">{role.replace("_", " ")}</h4>
              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{perms.length} perms</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {perms.map((p, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-mono">{p}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl" data-testid="role-change-card">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h4 className="font-bold text-amber-900">Change User Role (Super Admin only)</h4>
        </div>
        <div className="flex gap-2">
          <input value={changingId} onChange={(e) => setChangingId(e.target.value)} placeholder="User ID" className="flex-1 px-3 py-2 border border-amber-300 rounded-lg" data-testid="role-user-input" />
          <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="px-3 py-2 border border-amber-300 rounded-lg" data-testid="role-select">
            <option value="">Select role</option>
            {Object.keys(matrix).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={changeRole} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold" data-testid="change-role-btn">Update</button>
        </div>
      </div>
    </div>
  );
}

function TwoFAPanel() {
  const [status, setStatus] = useState({ enabled: false, pending_setup: false });
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");

  const load = () => api.get("/auth/2fa/status").then(r => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    const { data } = await api.post("/auth/2fa/setup");
    setSetup(data);
  };
  const verify = async () => {
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA enabled!");
      setSetup(null); setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
  };
  const disable = async () => {
    if (!code) return toast.error("Enter current 2FA code");
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA disabled");
      setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="twofa-panel">
      <div className="p-5 bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className={`w-6 h-6 ${status.enabled ? "text-emerald-600" : "text-slate-400"}`} />
          <div>
            <h3 className="font-bold text-[#1a3c5e]">Two-Factor Authentication</h3>
            <p className="text-sm text-slate-600">
              {status.enabled ? "Enabled — your account is protected" : "Add an extra layer of security with TOTP"}
            </p>
          </div>
        </div>

        {!status.enabled && !setup && (
          <button onClick={startSetup} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold" data-testid="2fa-setup-btn">
            Enable 2FA
          </button>
        )}

        {setup && (
          <div className="space-y-3 mt-3 p-4 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-700">1. Scan in Google Authenticator / Authy:</p>
            <code className="block p-2 bg-white border border-slate-200 rounded text-xs break-all">{setup.otpauth_uri}</code>
            <p className="text-sm text-slate-700">Or enter secret manually:</p>
            <code className="block p-2 bg-white border border-slate-200 rounded font-mono">{setup.secret}</code>
            <p className="text-sm text-slate-700">2. Enter the 6-digit code:</p>
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg font-mono text-center" data-testid="2fa-code-input" />
              <button onClick={verify} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold" data-testid="2fa-verify-btn">Verify</button>
            </div>
          </div>
        )}

        {status.enabled && (
          <div className="space-y-2 mt-3">
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Current 2FA code" maxLength={6} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg font-mono text-center" />
              <button onClick={disable} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold" data-testid="2fa-disable-btn">Disable</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HealthPanel() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/admin/security/health").then(r => setH(r.data)); }, []);
  if (!h) return <div>Loading...</div>;
  const items = [
    ["Resend Email", h.resend_configured ? "Configured ✓" : "Not configured", h.resend_configured],
    ["Active Sessions", h.active_sessions, true],
    ["Users with 2FA", `${h.users_with_2fa} / ${h.users_total}`, h.users_with_2fa > 0],
    ["OTPs Issued (24h)", h.otps_issued_24h, true],
    ["Audit Events (24h)", h.audit_events_24h, true],
    ["Rate Limit Buckets", h.rate_limit_buckets, true],
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="security-health">
      {items.map(([label, val, ok], i) => (
        <div key={i} className="p-4 bg-white rounded-xl border border-slate-200">
          <div className="text-xs text-slate-500">{label}</div>
          <div className={`text-xl font-bold ${ok ? "text-emerald-700" : "text-amber-700"}`}>{val}</div>
        </div>
      ))}
    </div>
  );
}

export default function SecurityCenter() {
  const [tab, setTab] = useState("health");
  return (
    <div className="space-y-5" data-testid="admin-security">
      <h2 className="text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
        <Shield className="w-6 h-6" /> Security Center
      </h2>
      <div className="flex flex-wrap gap-2">
        <Tab active={tab === "health"} onClick={() => setTab("health")} icon={ShieldCheck} label="Health" testId="tab-health" />
        <Tab active={tab === "sessions"} onClick={() => setTab("sessions")} icon={Monitor} label="Sessions" testId="tab-sessions" />
        <Tab active={tab === "audit"} onClick={() => setTab("audit")} icon={ScrollText} label="Audit Log" testId="tab-audit" />
        <Tab active={tab === "perms"} onClick={() => setTab("perms")} icon={ListChecks} label="Permissions" testId="tab-perms" />
        <Tab active={tab === "2fa"} onClick={() => setTab("2fa")} icon={KeyRound} label="My 2FA" testId="tab-2fa" />
      </div>

      {tab === "health" && <HealthPanel />}
      {tab === "sessions" && <SessionsPanel />}
      {tab === "audit" && <AuditPanel />}
      {tab === "perms" && <PermissionsPanel />}
      {tab === "2fa" && <TwoFAPanel />}
    </div>
  );
}
